import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { useUser } from "../../context/UserContext";
import SessionList from "../../components/SessionList";

const NEW_TECHNICIAN_INITIAL = {
  naam: "",
  email: "",
  wachtwoord: "",
  personeelsnummer: "",
  active: true,
  maxHoursPerWeek: "40",
  skillsText: "",
  certificationsText: "",
  availabilityNotes: "",
  notes: "",
  canCreateWorkorders: false,
  homeBaseScopeId: "",
  allowedScopeIds: [],
  assignToSelectedProject: true,
};

const NEW_GRANT_INITIAL = {
  userId: "",
  scopeId: "",
  startsAt: "",
  endsAt: "",
  reason: "",
};

const NEW_REQUEST_INITIAL = {
  naam: "",
  email: "",
  desiredRole: "monteur",
  reason: "",
  urgency: "normaal",
};

const REQUEST_ROLE_OPTIONS_PL = [
  { value: "monteur", label: "monteur" },
  { value: "werkvoorbereider", label: "werkvoorbereider" },
  { value: "projectleider", label: "projectleider" },
  { value: "viewer", label: "alleen kijken (viewer)" },
];

const REQUEST_ROLE_OPTIONS_ADMIN = [
  ...REQUEST_ROLE_OPTIONS_PL,
  { value: "manager", label: "manager" },
  { value: "controller", label: "controller" },
];

function draftFromUser(user) {
  const profile = user?.technicianProfile || {};
  return {
    personeelsnummer: user?.personeelsnummer || "",
    active: profile.active !== false,
    maxHoursPerWeek: String(profile.maxHoursPerWeek ?? 40),
    skillsText: toCsv(profile.skills),
    certificationsText: toCsv(profile.certifications),
    availabilityNotes: profile.availabilityNotes || "",
    notes: profile.notes || "",
    canCreateWorkorders: profile.canCreateWorkorders === true,
    homeBaseScopeId: String(user?.homeBaseScopeId || ""),
    allowedScopeIds: toIdArray(user?.allowedScopeIds),
  };
}

export default function MonteursPage() {
  const { user } = useUser();
  const [monteurs, setMonteurs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [scopeGrants, setScopeGrants] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [profileDrafts, setProfileDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [savingProfileId, setSavingProfileId] = useState("");
  const [assigningUserId, setAssigningUserId] = useState("");
  const [creatingTechnician, setCreatingTechnician] = useState(false);
  const [newTechnician, setNewTechnician] = useState(NEW_TECHNICIAN_INITIAL);
  const [showNewTechnicianPassword, setShowNewTechnicianPassword] = useState(false);
  const [newRequest, setNewRequest] = useState(NEW_REQUEST_INITIAL);
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [reviewingRequestId, setReviewingRequestId] = useState("");
  const [reviewNoteByRequest, setReviewNoteByRequest] = useState({});
  const [newGrant, setNewGrant] = useState(NEW_GRANT_INITIAL);
  const [creatingGrant, setCreatingGrant] = useState(false);
  const [revokingGrantId, setRevokingGrantId] = useState("");
  const [sendingResetUserId, setSendingResetUserId] = useState("");
  const [sessionManagerUser, setSessionManagerUser] = useState(null);
  const [openPanels, setOpenPanels] = useState({
    create: false,
    requests: false,
    grants: false,
  });
  const [expandedTechnicianIds, setExpandedTechnicianIds] = useState({});

  const roleNames = useMemo(() => getRoleNames(user), [user]);
  const canReviewRequests =
    roleNames.includes("tenantadmin") ||
    roleNames.includes("platformadmin") ||
    roleNames.includes("admin");
  const canDirectCreate = canReviewRequests;
  const requestRoleOptions = canReviewRequests
    ? REQUEST_ROLE_OPTIONS_ADMIN
    : REQUEST_ROLE_OPTIONS_PL;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setNotice("");
      const [monteurRes, projectRes, scopeRes, scopeGrantRes] = await Promise.all([
        api.get("/users?role=Monteur&includeSessionInsights=1"),
        api.get("/projects"),
        api.get("/tenant/scopes"),
        api.get("/tenant/scope-grants", { params: { active: "true" } }),
      ]);

      const monteurList = asArray(monteurRes.data);
      const projectList = Array.isArray(projectRes.data) ? projectRes.data : [];
      const scopeList = Array.isArray(scopeRes?.data?.items) ? scopeRes.data.items : [];
      const scopeGrantList = Array.isArray(scopeGrantRes?.data?.items) ? scopeGrantRes.data.items : [];
      setMonteurs(monteurList);
      setProjects(projectList);
      setScopes(scopeList);
      setScopeGrants(scopeGrantList);

      setProfileDrafts(
        monteurList.reduce((acc, user) => {
          acc[user._id] = draftFromUser(user);
          return acc;
        }, {})
      );

      setSelectedProjectId((prev) => {
        if (!prev && projectList.length > 0) {
          return String(projectList[0]._id);
        }
        if (prev && !projectList.some((p) => String(p._id) === String(prev))) {
          return projectList.length > 0 ? String(projectList[0]._id) : "";
        }
        return prev;
      });

      try {
        const requestRes = await api.get("/users/requests");
        setRequests(Array.isArray(requestRes?.data?.items) ? requestRes.data.items : []);
      } catch {
        setRequests([]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Monteurs laden mislukt");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedProject = useMemo(
    () => projects.find((p) => String(p._id) === String(selectedProjectId)) || null,
    [projects, selectedProjectId]
  );

  const memberIdSet = useMemo(() => {
    const ids = new Set();
    for (const member of selectedProject?.members || []) {
      const id = member?.userId?._id || member?.userId;
      if (id) ids.add(String(id));
    }
    return ids;
  }, [selectedProject]);

  function updateDraft(userId, key, value) {
    setProfileDrafts((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || draftFromUser({})),
        [key]: value,
      },
    }));
  }

  function togglePanel(key) {
    setOpenPanels((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }

  function toggleTechnicianDetails(userId) {
    setExpandedTechnicianIds((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  }

  async function persistProjectMembers(projectId, members) {
    const res = await api.put(`/projects/${projectId}/members`, {
      members: members.map((member) => ({
        userId: member?.userId?._id || member?.userId,
        role: member?.role || "monteur",
        permissions: Array.isArray(member?.permissions) ? member.permissions : [],
      })),
    });
    const updatedProject = res.data;
    setProjects((prev) =>
      prev.map((project) =>
        String(project._id) === String(updatedProject._id) ? updatedProject : project
      )
    );
    return updatedProject;
  }

  async function handleCreateTechnician(e) {
    e.preventDefault();
    const naam = String(newTechnician.naam || "").trim();
    const email = String(newTechnician.email || "").trim();
    const wachtwoord = String(newTechnician.wachtwoord || "");
    const personeelsnummer = String(newTechnician.personeelsnummer || "").trim();
    const maxHours = Number(newTechnician.maxHoursPerWeek || 0);

    if (!naam || !email || !wachtwoord) {
      setError("Naam, e-mail en wachtwoord zijn verplicht.");
      return;
    }
    if (wachtwoord.length < 8) {
      setError("Wachtwoord moet minimaal 8 tekens bevatten.");
      return;
    }
    if (!Number.isFinite(maxHours) || maxHours < 0) {
      setError("Max uren per week moet een geldig getal zijn.");
      return;
    }
    if (personeelsnummer.length > 50) {
      setError("Personeelsnummer mag maximaal 50 tekens bevatten.");
      return;
    }

    try {
      setCreatingTechnician(true);
      setError("");
      setNotice("");
      const payload = {
        naam,
        email,
        wachtwoord,
        ...(personeelsnummer ? { personeelsnummer } : {}),
        technicianProfile: {
          active: newTechnician.active !== false,
          maxHoursPerWeek: maxHours,
          skills: fromCsv(newTechnician.skillsText),
          certifications: fromCsv(newTechnician.certificationsText),
          availabilityNotes: String(newTechnician.availabilityNotes || "").trim(),
          notes: String(newTechnician.notes || "").trim(),
          canCreateWorkorders: newTechnician.canCreateWorkorders === true,
          homeBaseScopeId: newTechnician.homeBaseScopeId || null,
          allowedScopeIds: toIdArray(newTechnician.allowedScopeIds),
        },
      };

      const res = await api.post("/users/technicians", payload);
      const created = res?.data?.user;
      if (!created?._id) {
        throw new Error("Monteur aangemaakt, maar response is onvolledig");
      }

      setMonteurs((prev) => [created, ...prev]);
      setProfileDrafts((prev) => ({
        ...prev,
        [created._id]: draftFromUser(created),
      }));

      if (newTechnician.assignToSelectedProject && selectedProject) {
        try {
          const currentMembers = Array.isArray(selectedProject.members)
            ? selectedProject.members
            : [];
          const exists = currentMembers.some(
            (member) => String(member?.userId?._id || member?.userId) === String(created._id)
          );
          if (!exists) {
            await persistProjectMembers(selectedProject._id, [
              ...currentMembers,
              { userId: created._id, role: "monteur", permissions: [] },
            ]);
          }
        } catch (assignErr) {
          const detail =
            assignErr?.response?.data?.message ||
            "Request failed while assigning technician to project";
          setError(`Monteur is aangemaakt, maar inzetten op project is mislukt: ${detail}`);
        }
      }

      setNewTechnician((prev) => ({
        ...NEW_TECHNICIAN_INITIAL,
        assignToSelectedProject: prev.assignToSelectedProject,
      }));
    } catch (err) {
      if (err?.response?.status === 404 && String(err?.config?.url || "").includes("/users/technicians")) {
        setError(
          "Endpoint /api/users/technicians niet gevonden. Herstart de backend (node index.js) op de nieuwste code."
        );
      } else {
        setError(err?.response?.data?.message || err?.message || "Monteur aanmaken mislukt");
      }
    } finally {
      setCreatingTechnician(false);
    }
  }

  async function handleCreateRequest(e) {
    e.preventDefault();
    const naam = String(newRequest.naam || "").trim();
    const email = String(newRequest.email || "").trim().toLowerCase();
    const desiredRole = String(newRequest.desiredRole || "").trim().toLowerCase();
    const reason = String(newRequest.reason || "").trim();
    const urgency = String(newRequest.urgency || "normaal").trim().toLowerCase();

    if (!naam || !email || !desiredRole) {
      setError("Naam, e-mail en gewenste rol zijn verplicht.");
      return;
    }

    try {
      setCreatingRequest(true);
      setError("");
      setNotice("");
      const res = await api.post("/users/requests", {
        naam,
        email,
        desiredRole,
        reason,
        urgency,
      });
      const item = res?.data?.item;
      if (item?._id) {
        setRequests((prev) => [item, ...prev]);
      } else {
        await loadData();
      }
      setNewRequest(NEW_REQUEST_INITIAL);
    } catch (err) {
      setError(err?.response?.data?.message || "Gebruikersaanvraag indienen mislukt");
    } finally {
      setCreatingRequest(false);
    }
  }

  async function reviewRequest(requestId, decision) {
    if (!requestId || !decision) return;
    try {
      setReviewingRequestId(String(requestId));
      setError("");
      setNotice("");
      const reviewNote = String(reviewNoteByRequest[requestId] || "").trim();
      const res = await api.patch(`/users/requests/${requestId}/review`, {
        decision,
        reviewNote,
      });
      const updated = res?.data?.item;
      if (updated?._id) {
        setRequests((prev) =>
          prev.map((item) => (String(item._id) === String(updated._id) ? updated : item))
        );
      }
      const checkoutUrl = String(
        res?.data?.billingSeatCharge?.checkoutUrl || ""
      ).trim();
      if (checkoutUrl) {
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Aanvraag beoordelen mislukt");
    } finally {
      setReviewingRequestId("");
    }
  }

  async function toggleAccountBlock(userItem) {
    const userId = String(userItem?._id || "");
    if (!userId) return;
    const nextBlocked = !Boolean(userItem?.accountBlocked);
    const reason = nextBlocked
      ? window.prompt("Reden blokkade (optioneel):", "") || ""
      : "";

    try {
      setError("");
      setNotice("");
      await api.patch(`/users/${userId}/account-block`, {
        blocked: nextBlocked,
        reason,
      });
      setMonteurs((prev) =>
        prev.map((item) =>
          String(item._id) === userId
            ? {
                ...item,
                accountBlocked: nextBlocked,
                accountBlockReason: nextBlocked ? reason : "",
              }
            : item
        )
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Account status wijzigen mislukt");
    }
  }

  async function saveProfile(user) {
    const userId = String(user._id);
    const draft = profileDrafts[userId] || draftFromUser(user);
    const maxHours = Number(draft.maxHoursPerWeek || 0);
    const personeelsnummer = String(draft.personeelsnummer || "").trim();
    if (!Number.isFinite(maxHours) || maxHours < 0) {
      setError("Max uren per week moet een geldig getal zijn.");
      return;
    }
    if (personeelsnummer.length > 50) {
      setError("Personeelsnummer mag maximaal 50 tekens bevatten.");
      return;
    }

    try {
      setSavingProfileId(userId);
      setError("");
      setNotice("");
      const payload = {
        active: draft.active !== false,
        maxHoursPerWeek: maxHours,
        skills: fromCsv(draft.skillsText),
        certifications: fromCsv(draft.certificationsText),
        availabilityNotes: String(draft.availabilityNotes || "").trim(),
        notes: String(draft.notes || "").trim(),
        canCreateWorkorders: draft.canCreateWorkorders === true,
        homeBaseScopeId: draft.homeBaseScopeId || null,
        allowedScopeIds: toIdArray(draft.allowedScopeIds),
      };

      await api.put(`/users/${userId}/technician-profile`, {
        ...(personeelsnummer ? { personeelsnummer } : { personeelsnummer: "" }),
        technicianProfile: payload,
      });

      setMonteurs((prev) =>
        prev.map((item) =>
          String(item._id) === userId
            ? {
                ...item,
                personeelsnummer,
                technicianProfile: payload,
                homeBaseScopeId: payload.homeBaseScopeId || null,
                allowedScopeIds: payload.allowedScopeIds || [],
              }
            : item
        )
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Monteurprofiel opslaan mislukt");
    } finally {
      setSavingProfileId("");
    }
  }

  async function toggleProjectAssignment(user) {
    if (!selectedProject) {
      setError("Kies eerst een project.");
      return;
    }

    const userId = String(user._id);
    const currentMembers = Array.isArray(selectedProject.members)
      ? selectedProject.members
      : [];
    const alreadyMember = currentMembers.some(
      (member) => String(member?.userId?._id || member?.userId) === userId
    );

    const nextMembers = alreadyMember
      ? currentMembers.filter(
          (member) => String(member?.userId?._id || member?.userId) !== userId
        )
      : [...currentMembers, { userId, role: "monteur", permissions: [] }];

    try {
      setAssigningUserId(userId);
      setError("");
      setNotice("");
      await persistProjectMembers(selectedProject._id, nextMembers);
    } catch (err) {
      setError(err?.response?.data?.message || "Inzetten van monteur mislukt");
    } finally {
      setAssigningUserId("");
    }
  }

  async function createScopeGrant(e) {
    e.preventDefault();
    if (!newGrant.userId || !newGrant.scopeId || !newGrant.startsAt || !newGrant.endsAt) {
      setError("Kies monteur, scope en start/eind tijd voor tijdelijke vrijgave.");
      return;
    }
    try {
      setCreatingGrant(true);
      setError("");
      setNotice("");
      await api.post("/tenant/scope-grants", {
        userId: newGrant.userId,
        scopeId: newGrant.scopeId,
        startsAt: new Date(newGrant.startsAt).toISOString(),
        endsAt: new Date(newGrant.endsAt).toISOString(),
        reason: String(newGrant.reason || "").trim(),
      });
      setNewGrant(NEW_GRANT_INITIAL);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Tijdelijke vrijgave aanmaken mislukt");
    } finally {
      setCreatingGrant(false);
    }
  }

  async function revokeScopeGrant(grantId) {
    if (!grantId) return;
    try {
      setRevokingGrantId(String(grantId));
      setError("");
      setNotice("");
      await api.delete(`/tenant/scope-grants/${grantId}`);
      setScopeGrants((prev) => prev.filter((item) => String(item._id) !== String(grantId)));
    } catch (err) {
      setError(err?.response?.data?.message || "Tijdelijke vrijgave intrekken mislukt");
    } finally {
      setRevokingGrantId("");
    }
  }

  async function sendPasswordReset(userItem) {
    const userId = String(userItem?._id || "");
    if (!userId) return;
    try {
      setSendingResetUserId(userId);
      setError("");
      setNotice("");
      await api.post(`/users/${userId}/send-password-reset`, {});
      setNotice("Als dit e-mailadres bekend is, is er een resetlink verstuurd.");
    } catch (err) {
      setError(err?.response?.data?.message || "Resetmail versturen mislukt");
    } finally {
      setSendingResetUserId("");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Monteurs</h2>
        <p className="text-sm text-gray-600">
          Beheer monteurprofielen en zet monteurs direct in op een project.
        </p>
      </div>

      <section className="rounded-xl bg-white p-4 shadow">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm font-medium">Project voor inzet:</label>
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="rounded border p-2 text-sm min-w-[260px]"
          >
            <option value="">Kies project</option>
            {projects.map((project) => (
              <option key={project._id} value={project._id}>
                {project.projectNummer || project.number || "-"} - {project.naam || project.title || "-"}
              </option>
            ))}
          </select>
          <button
            onClick={loadData}
            className="rounded border px-3 py-2 text-sm hover:bg-gray-50"
          >
            Vernieuwen
          </button>
        </div>
        {selectedProject && (
          <p className="mt-2 text-xs text-gray-500">
            Ingezet op dit project: {memberIdSet.size} monteur(s)
          </p>
        )}
      </section>

      {canDirectCreate ? (
      <section className="rounded-xl bg-white p-4 shadow">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="font-semibold">Nieuwe monteur (direct)</h3>
          <button
            type="button"
            onClick={() => togglePanel("create")}
            className="rounded border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            {openPanels.create ? "Inklappen" : "Openen"}
          </button>
        </div>
        {openPanels.create ? (
        <form onSubmit={handleCreateTechnician} className="space-y-3">
          <div className="grid gap-2 md:grid-cols-3">
            <input
              value={newTechnician.naam}
              onChange={(e) =>
                setNewTechnician((prev) => ({ ...prev, naam: e.target.value }))
              }
              placeholder="Naam *"
              className="rounded border p-2 text-sm"
              required
            />
            <input
              type="email"
              value={newTechnician.email}
              onChange={(e) =>
                setNewTechnician((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="E-mail *"
              className="rounded border p-2 text-sm"
              required
            />
            <input
              value={newTechnician.personeelsnummer}
              onChange={(e) =>
                setNewTechnician((prev) => ({
                  ...prev,
                  personeelsnummer: e.target.value,
                }))
              }
              placeholder="Personeelsnummer"
              className="rounded border p-2 text-sm"
            />
            <div className="relative">
              <input
                type={showNewTechnicianPassword ? "text" : "password"}
                value={newTechnician.wachtwoord}
                onChange={(e) =>
                  setNewTechnician((prev) => ({ ...prev, wachtwoord: e.target.value }))
                }
                placeholder="Wachtwoord (min 8) *"
                className="w-full rounded border p-2 pr-20 text-sm"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewTechnicianPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 px-3 text-sm font-medium text-gray-600 hover:text-gray-900"
                aria-label={
                  showNewTechnicianPassword ? "Verberg wachtwoord" : "Toon wachtwoord"
                }
              >
                {showNewTechnicianPassword ? "Verberg" : "Toon"}
              </button>
            </div>
            <input
              type="number"
              min="0"
              max="168"
              value={newTechnician.maxHoursPerWeek}
              onChange={(e) =>
                setNewTechnician((prev) => ({ ...prev, maxHoursPerWeek: e.target.value }))
              }
              placeholder="Max uur/week"
              className="rounded border p-2 text-sm"
            />
            <input
              value={newTechnician.skillsText}
              onChange={(e) =>
                setNewTechnician((prev) => ({ ...prev, skillsText: e.target.value }))
              }
              placeholder="Skills (komma)"
              className="rounded border p-2 text-sm"
            />
            <input
              value={newTechnician.certificationsText}
              onChange={(e) =>
                setNewTechnician((prev) => ({
                  ...prev,
                  certificationsText: e.target.value,
                }))
              }
              placeholder="Certificaten (komma)"
              className="rounded border p-2 text-sm"
            />
            <input
              value={newTechnician.availabilityNotes}
              onChange={(e) =>
                setNewTechnician((prev) => ({
                  ...prev,
                  availabilityNotes: e.target.value,
                }))
              }
              placeholder="Beschikbaarheid"
              className="rounded border p-2 text-sm md:col-span-2"
            />
            <input
              value={newTechnician.notes}
              onChange={(e) =>
                setNewTechnician((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="Notities"
              className="rounded border p-2 text-sm"
            />
            <select
              value={newTechnician.homeBaseScopeId}
              onChange={(e) =>
                setNewTechnician((prev) => ({ ...prev, homeBaseScopeId: e.target.value }))
              }
              className="rounded border p-2 text-sm"
            >
              <option value="">Uitvalbasis (optioneel)</option>
              {scopes.map((scope) => (
                <option key={scope._id} value={scope._id}>
                  {formatScopeLabel(scope)}
                </option>
              ))}
            </select>
            <label className="rounded border p-2 text-sm md:col-span-2">
              <span className="mb-1 block text-xs text-gray-600">Inzetbare scopes/regio's</span>
              <select
                multiple
                value={newTechnician.allowedScopeIds}
                onChange={(e) =>
                  setNewTechnician((prev) => ({
                    ...prev,
                    allowedScopeIds: Array.from(e.target.selectedOptions).map((opt) => opt.value),
                  }))
                }
                className="h-24 w-full rounded border p-2 text-sm"
              >
                {scopes.map((scope) => (
                  <option key={scope._id} value={scope._id}>
                    {formatScopeLabel(scope)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newTechnician.active !== false}
                onChange={(e) =>
                  setNewTechnician((prev) => ({ ...prev, active: e.target.checked }))
                }
              />
              Actief profiel
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newTechnician.canCreateWorkorders === true}
                onChange={(e) =>
                  setNewTechnician((prev) => ({
                    ...prev,
                    canCreateWorkorders: e.target.checked,
                  }))
                }
              />
              Mag zelfstandig werkbon aanmaken
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newTechnician.assignToSelectedProject}
                onChange={(e) =>
                  setNewTechnician((prev) => ({
                    ...prev,
                    assignToSelectedProject: e.target.checked,
                  }))
                }
                disabled={!selectedProjectId}
              />
              Direct inzetten op geselecteerd project
            </label>
            <button
              type="submit"
              disabled={creatingTechnician}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {creatingTechnician ? "Aanmaken..." : "Monteur aanmaken"}
            </button>
          </div>
        </form>
        ) : (
          <p className="text-sm text-gray-500">
            Maak alleen open wanneer je echt een nieuwe monteur toevoegt.
          </p>
        )}
      </section>
      ) : (
      <section className="rounded-xl bg-white p-4 shadow">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Gebruiker aanvragen</h3>
            <p className="text-sm text-gray-600">
              Vraag een nieuwe gebruiker aan. Tenant Admin keurt de aanvraag daarna goed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => togglePanel("create")}
            className="rounded border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            {openPanels.create ? "Inklappen" : "Openen"}
          </button>
        </div>
        {openPanels.create ? (
        <form onSubmit={handleCreateRequest} className="space-y-3">
          <div className="grid gap-2 md:grid-cols-3">
            <input
              value={newRequest.naam}
              onChange={(e) => setNewRequest((prev) => ({ ...prev, naam: e.target.value }))}
              placeholder="Naam *"
              className="rounded border p-2 text-sm"
              required
            />
            <input
              type="email"
              value={newRequest.email}
              onChange={(e) => setNewRequest((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="E-mail *"
              className="rounded border p-2 text-sm"
              required
            />
            <select
              value={newRequest.desiredRole}
              onChange={(e) =>
                setNewRequest((prev) => ({ ...prev, desiredRole: e.target.value }))
              }
              className="rounded border p-2 text-sm"
            >
              {requestRoleOptions.map((roleOption) => (
                <option key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </option>
              ))}
            </select>
            <select
              value={newRequest.urgency}
              onChange={(e) => setNewRequest((prev) => ({ ...prev, urgency: e.target.value }))}
              className="rounded border p-2 text-sm"
            >
              <option value="laag">Urgentie: laag</option>
              <option value="normaal">Urgentie: normaal</option>
              <option value="hoog">Urgentie: hoog</option>
              <option value="kritiek">Urgentie: kritiek</option>
            </select>
            <input
              value={newRequest.reason}
              onChange={(e) => setNewRequest((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Reden aanvraag"
              className="rounded border p-2 text-sm md:col-span-2"
            />
          </div>

          <button
            type="submit"
            disabled={creatingRequest}
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {creatingRequest ? "Indienen..." : "Aanvraag indienen"}
          </button>
        </form>
        ) : null}
      </section>
      )}

      <section className="rounded-xl bg-white p-4 shadow">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="font-semibold">Gebruikersaanvragen</h3>
          <button
            type="button"
            onClick={() => togglePanel("requests")}
            className="rounded border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            {openPanels.requests ? "Inklappen" : `Openen${requests.length ? ` (${requests.length})` : ""}`}
          </button>
        </div>
        {!openPanels.requests ? (
          <p className="text-sm text-gray-500">
            {requests.length === 0
              ? "Geen openstaande aanvragen."
              : `${requests.length} aanvraag/aanvragen verborgen.`}
          </p>
        ) : requests.length === 0 ? (
          <p className="text-sm text-gray-500">Geen aanvragen gevonden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-3">Aanvraag</th>
                  <th className="py-2 pr-3">Rol</th>
                  <th className="py-2 pr-3">Urgentie</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Aangevraagd door</th>
                  <th className="py-2 pr-3">Toelichting</th>
                  {canReviewRequests ? <th className="py-2 text-right">Acties</th> : null}
                </tr>
              </thead>
              <tbody>
                {requests.map((item) => {
                  const canReview = canReviewRequests && String(item.status) === "nieuw";
                  return (
                    <tr key={item._id} className="border-b align-top">
                      <td className="py-2 pr-3">
                        <p className="font-medium">{item.naam || "-"}</p>
                        <p className="text-xs text-gray-500">{item.email || "-"}</p>
                      </td>
                      <td className="py-2 pr-3">{item.desiredRole || "-"}</td>
                      <td className="py-2 pr-3">{item.urgency || "-"}</td>
                      <td className="py-2 pr-3">{renderRequestStatus(item.status)}</td>
                      <td className="py-2 pr-3">{item.requestedByName || "-"}</td>
                      <td className="py-2 pr-3 max-w-[280px]">
                        <p className="truncate">{item.reason || "-"}</p>
                        {item.reviewNote ? (
                          <p className="mt-1 text-xs text-gray-500">Review: {item.reviewNote}</p>
                        ) : null}
                      </td>
                      {canReviewRequests ? (
                        <td className="py-2 text-right">
                          {canReview ? (
                            <div className="flex justify-end gap-2">
                              <input
                                type="text"
                                value={reviewNoteByRequest[item._id] || ""}
                                onChange={(e) =>
                                  setReviewNoteByRequest((prev) => ({
                                    ...prev,
                                    [item._id]: e.target.value,
                                  }))
                                }
                                className="w-44 rounded border p-1.5 text-xs"
                                placeholder="Review notitie"
                              />
                              <button
                                onClick={() => reviewRequest(item._id, "goedgekeurd")}
                                disabled={reviewingRequestId === String(item._id)}
                                className="rounded bg-green-600 px-2.5 py-1.5 text-xs text-white hover:bg-green-700 disabled:opacity-60"
                              >
                                Goedkeuren
                              </button>
                              <button
                                onClick={() => reviewRequest(item._id, "afgewezen")}
                                disabled={reviewingRequestId === String(item._id)}
                                className="rounded bg-red-600 px-2.5 py-1.5 text-xs text-white hover:bg-red-700 disabled:opacity-60"
                              >
                                Afwijzen
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">
                              {item.reviewedByName ? `Door: ${item.reviewedByName}` : "-"}
                            </span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-xl bg-white p-4 shadow">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="font-semibold">Tijdelijke scope-vrijgave (lenen)</h3>
          <button
            type="button"
            onClick={() => togglePanel("grants")}
            className="rounded border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            {openPanels.grants ? "Inklappen" : `Openen${scopeGrants.length ? ` (${scopeGrants.length})` : ""}`}
          </button>
        </div>
        {openPanels.grants ? (
        <>
        <form onSubmit={createScopeGrant} className="grid gap-2 md:grid-cols-5">
          <select
            value={newGrant.userId}
            onChange={(e) => setNewGrant((prev) => ({ ...prev, userId: e.target.value }))}
            className="rounded border p-2 text-sm"
          >
            <option value="">Monteur</option>
            {monteurs.map((item) => (
              <option key={item._id} value={item._id}>
                {item.naam || item.email || item._id}
              </option>
            ))}
          </select>
          <select
            value={newGrant.scopeId}
            onChange={(e) => setNewGrant((prev) => ({ ...prev, scopeId: e.target.value }))}
            className="rounded border p-2 text-sm"
          >
            <option value="">Scope/regio</option>
            {scopes.map((scope) => (
              <option key={scope._id} value={scope._id}>
                {formatScopeLabel(scope)}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            value={newGrant.startsAt}
            onChange={(e) => setNewGrant((prev) => ({ ...prev, startsAt: e.target.value }))}
            className="rounded border p-2 text-sm"
          />
          <input
            type="datetime-local"
            value={newGrant.endsAt}
            onChange={(e) => setNewGrant((prev) => ({ ...prev, endsAt: e.target.value }))}
            className="rounded border p-2 text-sm"
          />
          <button
            type="submit"
            disabled={creatingGrant}
            className="rounded bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {creatingGrant ? "Opslaan..." : "Vrijgave opslaan"}
          </button>
          <input
            value={newGrant.reason}
            onChange={(e) => setNewGrant((prev) => ({ ...prev, reason: e.target.value }))}
            placeholder="Reden (optioneel)"
            className="rounded border p-2 text-sm md:col-span-5"
          />
        </form>

        {scopeGrants.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">Geen actieve tijdelijke vrijgaves.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-3">Monteur</th>
                  <th className="py-2 pr-3">Scope</th>
                  <th className="py-2 pr-3">Van</th>
                  <th className="py-2 pr-3">Tot</th>
                  <th className="py-2 pr-3">Reden</th>
                  <th className="py-2 text-right">Actie</th>
                </tr>
              </thead>
              <tbody>
                {scopeGrants.map((grant) => (
                  <tr key={grant._id} className="border-b">
                    <td className="py-2 pr-3">{grant?.userId?.naam || grant?.userId?.email || "-"}</td>
                    <td className="py-2 pr-3">{formatScopeLabel(grant?.scopeId)}</td>
                    <td className="py-2 pr-3">{formatDateTime(grant?.startsAt)}</td>
                    <td className="py-2 pr-3">{formatDateTime(grant?.endsAt)}</td>
                    <td className="py-2 pr-3">{grant?.reason || "-"}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => revokeScopeGrant(grant._id)}
                        disabled={revokingGrantId === String(grant._id)}
                        className="rounded bg-rose-600 px-3 py-1.5 text-xs text-white hover:bg-rose-700 disabled:opacity-60"
                      >
                        {revokingGrantId === String(grant._id) ? "Bezig..." : "Intrekken"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        ) : (
          <p className="text-sm text-gray-500">
            {scopeGrants.length === 0
              ? "Geen actieve tijdelijke vrijgaves."
              : `${scopeGrants.length} actieve vrijgave(s) verborgen.`}
          </p>
        )}
      </section>

      <section className="rounded-xl bg-white p-4 shadow">
        <h3 className="mb-3 font-semibold">Monteurprofielen</h3>
        {loading && <p className="text-sm text-gray-500">Laden...</p>}
        {loading && <p className="text-sm text-gray-500">Sessie-informatie laden...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {notice && <p className="text-sm text-slate-700">{notice}</p>}
        {!loading && !error && monteurs.length === 0 && (
          <p className="text-sm text-gray-500">Geen monteurs gevonden.</p>
        )}

        {!loading && monteurs.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              Sessiesignalen zijn informatief en blokkeren geen acties.
              Meerdere actieve sessies, mogelijk verouderde sessie en nieuw apparaat worden alleen getoond als relevant.
            </p>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-3">Monteur</th>
                  <th className="py-2 pr-3">Accountstatus</th>
                  <th className="py-2 pr-3 hidden md:table-cell">Project</th>
                  <th className="py-2 pr-3 hidden md:table-cell">Sessie</th>
                  <th className="py-2 pr-3">Planning</th>
                  <th className="py-2 pr-3 hidden lg:table-cell">Profiel</th>
                  <th className="py-2 text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {monteurs.map((user) => {
                  const userId = String(user._id);
                  const draft = profileDrafts[userId] || draftFromUser(user);
                  const assigned = memberIdSet.has(userId);
                  const busyProfile = savingProfileId === userId;
                  const busyAssign = assigningUserId === userId;
                  const isExpanded = expandedTechnicianIds[userId] === true;

                  return (
                    <Fragment key={userId}>
                      <tr className="border-b align-top">
                        <td className="py-2 pr-3">
                          <p className="font-medium">{user.naam || "-"}</p>
                          <p className="text-xs text-gray-500">{user.email || "-"}</p>
                          <p className="mt-1 text-xs text-gray-500">
                            Nr: {draft.personeelsnummer || "-"}
                          </p>
                          {user.accountBlocked ? (
                            <p className="mt-1 text-xs text-red-600">
                              Geblokkeerd{user.accountBlockReason ? `: ${user.accountBlockReason}` : ""}
                            </p>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="space-y-1">
                            {renderUserStatus(user)}
                            <div className="text-xs text-slate-500">
                              Laatste login: {formatDateTime(user.lastLoginAt)}
                            </div>
                          </div>
                        </td>
                        <td className="py-2 pr-3 hidden md:table-cell">
                          <span
                            className={`rounded px-2 py-0.5 text-xs ${
                              assigned
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {assigned ? "In project" : "Niet ingezet"}
                          </span>
                        </td>
                        <td className="py-2 pr-3 hidden md:table-cell">
                          <div className="space-y-1">
                            {renderSessionStatus(user)}
                            <div className="text-xs text-slate-500">
                              {Number(user.activeSessionCount || 0)} actieve sessie(s)
                            </div>
                            <div>{renderSessionSignals(user)}</div>
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="space-y-1 text-xs text-slate-600">
                            <p>{draft.maxHoursPerWeek || "0"} u/week</p>
                            <p>{draft.canCreateWorkorders ? "Zelf werkbon: ja" : "Zelf werkbon: nee"}</p>
                            <p>{draft.active !== false ? "Profiel actief" : "Profiel inactief"}</p>
                          </div>
                        </td>
                        <td className="py-2 pr-3 hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {renderCompactMeta("Skills", draft.skillsText)}
                            {renderCompactMeta("Cert", draft.certificationsText)}
                            {renderCompactMeta("Scopes", toIdArray(draft.allowedScopeIds).length)}
                            {renderCompactMeta(
                              "Uitvalbasis",
                              draft.homeBaseScopeId
                                ? formatScopeLabel(scopes.find((scope) => String(scope._id) === String(draft.homeBaseScopeId)))
                                : "-"
                            )}
                          </div>
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              onClick={() => toggleTechnicianDetails(userId)}
                              className="rounded border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                            >
                              {isExpanded ? "Minder" : "Bewerk"}
                            </button>
                            <button
                              onClick={() => toggleProjectAssignment(user)}
                              disabled={!selectedProjectId || busyAssign}
                              className={`rounded px-3 py-1.5 text-xs text-white disabled:opacity-60 ${
                                assigned
                                  ? "bg-red-600 hover:bg-red-700"
                                  : "bg-green-600 hover:bg-green-700"
                              }`}
                            >
                              {busyAssign
                                ? "Bezig..."
                                : assigned
                                ? "Uitzetten"
                                : "Inzetten"}
                            </button>
                            <button
                              onClick={() => setSessionManagerUser(user)}
                              className="rounded bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700"
                            >
                              Sessies
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr className="border-b bg-slate-50/70">
                          <td colSpan={7} className="p-3">
                            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                              <label className="space-y-1 text-sm">
                                <span className="block text-xs font-medium text-slate-600">Personeelsnummer</span>
                                <input
                                  type="text"
                                  value={draft.personeelsnummer}
                                  onChange={(e) => updateDraft(userId, "personeelsnummer", e.target.value)}
                                  className="w-full rounded border p-2"
                                  placeholder="bijv. M-1004"
                                />
                              </label>
                              <label className="space-y-1 text-sm">
                                <span className="block text-xs font-medium text-slate-600">Max uur/week</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="168"
                                  value={draft.maxHoursPerWeek}
                                  onChange={(e) => updateDraft(userId, "maxHoursPerWeek", e.target.value)}
                                  className="w-full rounded border p-2"
                                />
                              </label>
                              <label className="space-y-1 text-sm">
                                <span className="block text-xs font-medium text-slate-600">Uitvalbasis</span>
                                <select
                                  value={draft.homeBaseScopeId || ""}
                                  onChange={(e) => updateDraft(userId, "homeBaseScopeId", e.target.value)}
                                  className="w-full rounded border p-2"
                                >
                                  <option value="">Geen uitvalbasis</option>
                                  {scopes.map((scope) => (
                                    <option key={scope._id} value={scope._id}>
                                      {formatScopeLabel(scope)}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="space-y-1 text-sm xl:col-span-2">
                                <span className="block text-xs font-medium text-slate-600">Skills (komma)</span>
                                <input
                                  type="text"
                                  value={draft.skillsText}
                                  onChange={(e) => updateDraft(userId, "skillsText", e.target.value)}
                                  className="w-full rounded border p-2"
                                  placeholder="brand, utiliteit"
                                />
                              </label>
                              <label className="space-y-1 text-sm">
                                <span className="block text-xs font-medium text-slate-600">Certificaten (komma)</span>
                                <input
                                  type="text"
                                  value={draft.certificationsText}
                                  onChange={(e) => updateDraft(userId, "certificationsText", e.target.value)}
                                  className="w-full rounded border p-2"
                                  placeholder="VCA, BHV"
                                />
                              </label>
                              <label className="space-y-1 text-sm xl:col-span-2">
                                <span className="block text-xs font-medium text-slate-600">Beschikbaarheid</span>
                                <input
                                  type="text"
                                  value={draft.availabilityNotes}
                                  onChange={(e) => updateDraft(userId, "availabilityNotes", e.target.value)}
                                  className="w-full rounded border p-2"
                                  placeholder="bijv. ma-do beschikbaar"
                                />
                              </label>
                              <label className="space-y-1 text-sm xl:col-span-3">
                                <span className="block text-xs font-medium text-slate-600">Notities</span>
                                <input
                                  type="text"
                                  value={draft.notes}
                                  onChange={(e) => updateDraft(userId, "notes", e.target.value)}
                                  className="w-full rounded border p-2"
                                  placeholder="extra notities"
                                />
                              </label>
                              <label className="space-y-1 text-sm xl:col-span-3">
                                <span className="block text-xs font-medium text-slate-600">Inzetbare scopes</span>
                                <select
                                  multiple
                                  value={toIdArray(draft.allowedScopeIds)}
                                  onChange={(e) =>
                                    updateDraft(
                                      userId,
                                      "allowedScopeIds",
                                      Array.from(e.target.selectedOptions).map((opt) => opt.value)
                                    )
                                  }
                                  className="h-28 w-full rounded border p-2"
                                >
                                  {scopes.map((scope) => (
                                    <option key={scope._id} value={scope._id}>
                                      {formatScopeLabel(scope)}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-4">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={draft.active !== false}
                                  onChange={(e) => updateDraft(userId, "active", e.target.checked)}
                                />
                                Actief profiel
                              </label>
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={draft.canCreateWorkorders === true}
                                  onChange={(e) =>
                                    updateDraft(userId, "canCreateWorkorders", e.target.checked)
                                  }
                                />
                                Mag zelfstandig werkbon aanmaken
                              </label>
                            </div>
                            <div className="mt-4 flex flex-wrap justify-end gap-2">
                              <button
                                onClick={() => saveProfile(user)}
                                disabled={busyProfile}
                                className="rounded bg-indigo-600 px-3 py-1.5 text-xs text-white hover:bg-indigo-700 disabled:opacity-60"
                              >
                                {busyProfile ? "Opslaan..." : "Profiel opslaan"}
                              </button>
                              {canReviewRequests ? (
                                <button
                                  onClick={() => toggleAccountBlock(user)}
                                  className={`rounded px-3 py-1.5 text-xs text-white ${
                                    user.accountBlocked
                                      ? "bg-emerald-600 hover:bg-emerald-700"
                                      : "bg-slate-700 hover:bg-slate-800"
                                  }`}
                                >
                                  {user.accountBlocked ? "Deblokkeren" : "Blokkeren"}
                                </button>
                              ) : null}
                              <button
                                onClick={() => sendPasswordReset(user)}
                                disabled={sendingResetUserId === userId}
                                className="rounded bg-slate-700 px-3 py-1.5 text-xs text-white hover:bg-slate-800 disabled:opacity-60"
                              >
                                {sendingResetUserId === userId ? "Versturen..." : "Wachtwoord reset sturen"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
        )}
      </section>

      {sessionManagerUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Sessies beheren
                </h3>
                <p className="text-sm text-slate-600">
                  {sessionManagerUser.naam || "-"} ({sessionManagerUser.email || "-"})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSessionManagerUser(null)}
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Sluiten
              </button>
            </div>
            <SessionList userId={sessionManagerUser._id} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function asArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  return [];
}

function getRoleNames(user) {
  const names = new Set();
  if (user?.rol) names.add(String(user.rol).trim().toLowerCase());
  if (user?.role) names.add(String(user.role).trim().toLowerCase());
  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") {
      names.add(String(role).trim().toLowerCase());
      continue;
    }
    const name = role?.name || role?.naam;
    if (name) names.add(String(name).trim().toLowerCase());
  }
  return Array.from(names);
}

function renderRequestStatus(value) {
  const status = String(value || "").trim().toLowerCase();
  if (status === "goedgekeurd") {
    return <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">Goedgekeurd</span>;
  }
  if (status === "afgewezen") {
    return <span className="rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800">Afgewezen</span>;
  }
  return <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Nieuw</span>;
}

function renderUserStatus(user) {
  if (user?.accountBlocked) {
    return <span className="rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800">Geblokkeerd</span>;
  }
  const approval = String(user?.approvalStatus || "approved").trim().toLowerCase();
  if (approval === "pending_approval") {
    return <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Wacht op goedkeuring</span>;
  }
  if (approval === "rejected") {
    return <span className="rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800">Afgewezen</span>;
  }
  return <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">Actief</span>;
}

function renderSessionStatus(user) {
  if (user?.hasActiveSession) {
    return <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">Actief</span>;
  }
  return <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">Geen actieve sessie</span>;
}

function renderSessionSignals(user) {
  const items = [];
  if (user?.hasMultipleActiveSessions) {
    items.push("Meerdere actieve sessies");
  }
  if (user?.hasStaleActiveSession) {
    items.push("Mogelijk verouderde sessie");
  }
  if (user?.hasRecentUnknownDevice) {
    items.push("Nieuw apparaat gedetecteerd");
  }

  if (!items.length) {
    return <span className="text-xs text-slate-400">-</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {items.map((label) => (
        <span
          key={label}
          className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
          title={label}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function renderCompactMeta(label, value) {
  const normalized =
    typeof value === "number"
      ? String(value)
      : String(value || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .join(", ");

  const shortValue =
    normalized.length > 28 ? `${normalized.slice(0, 28).trim()}...` : normalized || "-";

  return (
    <span
      className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
      title={`${label}: ${normalized || "-"}`}
    >
      {label}: {shortValue}
    </span>
  );
}

function toCsv(values) {
  if (!Array.isArray(values)) return "";
  return values
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(", ");
}

function fromCsv(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function toIdArray(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item?._id || item || "").trim())
    .filter(Boolean);
}

function formatScopeLabel(scope) {
  if (!scope) return "-";
  const type = String(scope?.type || "").trim();
  const name = String(scope?.name || "").trim();
  const code = String(scope?.code || "").trim();
  if (type && name && code) return `${type} • ${name} (${code})`;
  if (type && name) return `${type} • ${name}`;
  return name || code || "-";
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
